/**
 * Roles.
 *
 * WHAT THIS PAGE IS FOR, and it is not "configuring permissions". It is for the
 * moment somebody looks at the five built-in roles and none of them is the job
 * the person in front of them actually does. Their move today is to round up -
 * give the auditor Admin because Read Only is too little - and that single
 * decision, repeated, is how an organisation ends up with six admins.
 *
 * So the page leads with the built-ins as a reference, and the custom roles
 * underneath as the answer.
 *
 * THE ONE INTERACTION RULE THAT MATTERS. A capability the CALLER does not hold
 * is shown, disabled, with the reason on hover - not hidden. Hiding it would
 * make an incomplete list look complete, and somebody would build a role
 * believing it covers everything and hand it to a person it under-serves. The
 * server refuses these anyway; showing them is how the refusal stops being a
 * surprise.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

interface RoleView {
  id: string;
  name: string;
  description?: string;
  capabilities: string[];
}

interface RolesPayload {
  capabilities: string[];
  reserved: string[];
  builtIn: RoleView[];
  custom: RoleView[];
  youHold: string[];
}

const getRoles = call<{ orgId: string }, RolesPayload>('getRoles');
const saveCustomRole = call<
  { orgId: string; id?: string; name: string; description?: string; capabilities: string[] },
  { ok: boolean; role: RoleView; refused: string[] }
>('saveCustomRole');
const deleteCustomRole = call<{ orgId: string; id: string }, { ok: boolean }>('deleteCustomRole');
/**
 * Adjusting a BUILT-IN role for this organisation only.
 *
 * Came across from Team Members with the editor. It is a different mechanism
 * from custom roles on purpose: a built-in is a shipped template plus this
 * org's deltas, so an upgrade that adds a capability reaches every tenant who
 * has not deliberately opted out of it. A custom role has no template to
 * inherit from and would not want one.
 */
const setRoleOverrides =
  call<{ orgId: string; overrides: Record<string, Record<string, boolean>> }, { ok: boolean }>('setRoleOverrides');

/**
 * Plain English for each capability.
 *
 * The stored value is a dotted identifier because that is what code wants to
 * read. Nobody ticking a box should have to translate `anomalies.resolve` into
 * a decision about their colleague's job.
 */
const CAP_LABEL: Record<string, string> = {
  'org.manage': 'Rename or delete the organisation',
  'members.manage': 'Invite people and change their roles',
  'integrations.manage': 'Change vendor credentials',
  'alerts.manage': 'Change who gets alerted, and when',
  'targets.hide': 'Hide a machine from the board and every count',
  'targets.retire': 'Retire a machine, or bring one back',
  'anomalies.resolve': 'Work the Review queue',
  'policies.manage': 'Change what a backup is expected to do',
  'ticketing.manage': 'Change PSA settings and client mapping',
  'reports.send': 'Send client reports and schedule them',
  'knowledge.write': 'Write knowledge articles and signatures',
  'clients.portal': 'Invite client users and set what they see',
  'clients.viewAll': 'See every client, not only their own',
  'dashboard.view': 'Open the dashboard',
};

const CAP_NOTE: Record<string, string> = {
  'targets.hide':
    'The one action that can make a real failure invisible to everybody else. Not a day-to-day tool.',
  'clients.viewAll':
    'Without this, a person sees only the clients they are scoped to.',
  'clients.portal':
    'Deliberately separate from inviting staff - this exposes a client’s estate to an address somebody typed.',
  'integrations.manage':
    'Vendor credentials. The line between Admin and Manager has always been here.',
};

export function Roles({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [data, setData] = useState<RolesPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** The role being composed. Null when nothing is open. */
  const [draft, setDraft] = useState<RoleView | null>(null);
  const [editBuiltIn, setEditBuiltIn] = useState(false);

  const load = async () => {
    if (!orgId) return;
    try { setData(await getRoles({ orgId })); }
    catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  if (!orgId) return null;
  if (!data) return <div className="empty">Loading roles</div>;

  const holds = new Set(data.youHold);
  const reserved = new Set(data.reserved);

  const toggle = (cap: string) => {
    if (!draft) return;
    const has = draft.capabilities.includes(cap);
    setDraft({
      ...draft,
      capabilities: has ? draft.capabilities.filter((c) => c !== cap) : [...draft.capabilities, cap],
    });
  };

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setErr(null); setMsg(null);
    try { setMsg(await fn()); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  return (
    <div className="settings">
      <section className="panel">
        <h2 className="panel-title">Roles</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          The five built-in roles are the shapes that ship with the product. When none of them is
          the job somebody actually does, make one rather than rounding up &mdash; giving an auditor
          Admin because Read Only is too little is how an organisation ends up with six admins.
        </p>
        {err && <div className="banner banner-error">{err}</div>}
        {msg && <div className="banner banner-ok">{msg}</div>}
      </section>

      <section className="panel">
        <h2 className="panel-title">
          Built in
          <button className="btn-link" style={{ marginLeft: 12, fontSize: 12 }}
                  onClick={() => setEditBuiltIn(!editBuiltIn)}>
            {editBuiltIn ? 'done' : 'adjust'}
          </button>
        </h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          These cannot be deleted, but what they grant can be adjusted for this organisation
          &mdash; every other organisation keeps the shipped template. Owner is deliberately
          fixed: a restriction that locked the last owner out of this page would have no way back.
        </p>

        {!editBuiltIn ? (
          <table className="mini-table">
            <thead><tr><th style={{ width: 130 }}>Role</th><th>Can do</th></tr></thead>
            <tbody>
              {data.builtIn.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.name}</strong></td>
                  <td className="dim" style={{ fontSize: 12.5 }}>
                    {r.id === 'owner'
                      ? 'Everything, always.'
                      : r.capabilities.map((c) => CAP_LABEL[c] ?? c).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="role-grid">
            {data.builtIn.filter((r) => r.id !== 'owner').map((r) => (
              <div className="role-card" key={r.id}>
                <div className="role-head"><span className="role-name">{r.name}</span></div>
                <div className="role-desc">{r.description}</div>
                <div className="role-caps">
                  {data.capabilities.map((cap) => (
                    <label className="check-line" key={cap}>
                      <input
                        type="checkbox" disabled={!canManage || busy !== null}
                        checked={r.capabilities.includes(cap)}
                        onChange={(e) => void run('ov', async () => {
                          await setRoleOverrides({
                            orgId: orgId!,
                            overrides: { [r.id]: { [cap]: e.target.checked } },
                          });
                          return `${r.name}: ${CAP_LABEL[cap] ?? cap} ${e.target.checked ? 'granted' : 'revoked'}.`;
                        })}
                      />
                      <span>{CAP_LABEL[cap] ?? cap}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="panel">
        <h2 className="panel-title">
          Your own roles
          {data.custom.length > 0 && (
            <span className="tag tag-ok" style={{ marginLeft: 8 }}>{data.custom.length}</span>
          )}
        </h2>

        {data.custom.length === 0 && !draft && (
          <div className="empty">No custom roles yet.</div>
        )}

        {data.custom.length > 0 && (
          <table className="mini-table">
            <thead><tr><th style={{ width: 150 }}>Role</th><th>Can do</th><th style={{ width: 120 }}></th></tr></thead>
            <tbody>
              {data.custom.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.name}</strong>
                    {r.description && <div className="dim" style={{ fontSize: 12 }}>{r.description}</div>}
                  </td>
                  <td className="dim" style={{ fontSize: 12.5 }}>
                    {r.capabilities.map((c) => CAP_LABEL[c] ?? c).join(' · ')}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-link" disabled={!canManage}
                            onClick={() => setDraft({ ...r })}>Edit</button>
                    {' '}
                    <button className="btn-link" disabled={!canManage || busy !== null}
                            onClick={() => void run('d' + r.id, async () => {
                              await deleteCustomRole({ orgId: orgId!, id: r.id });
                              return `${r.name} deleted.`;
                            })}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!draft && canManage && (
          <button className="btn" style={{ marginTop: 12 }}
                  onClick={() => setDraft({ id: '', name: '', description: '', capabilities: ['dashboard.view'] })}>
            New role
          </button>
        )}

        {draft && (
          <div className="role-editor">
            <div className="row-form">
              <label className="field">
                <span className="field-label">Name</span>
                <input className="field-input" style={{ maxWidth: 240 }} value={draft.name}
                       placeholder="Territory Manager"
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              <label className="field" style={{ flex: '1 1 320px' }}>
                <span className="field-label">What this role is for</span>
                <input className="field-input" value={draft.description ?? ''}
                       placeholder="Looks after one region, cannot touch credentials"
                       onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              </label>
            </div>

            <div className="flbl" style={{ marginTop: 14 }}>What it can do</div>
            <div className="cap-grid">
              {data.capabilities.map((cap) => {
                const isReserved = reserved.has(cap);
                const youLack = !holds.has(cap);
                const locked = isReserved || youLack;
                const why = isReserved
                  ? 'Reserved for the built-in roles. Owning the organisation and deciding who else can are not things a custom role may hold.'
                  : youLack
                    ? 'You do not hold this yourself, so you cannot grant it.'
                    : CAP_NOTE[cap] ?? '';
                return (
                  <label className={'cap' + (locked ? ' locked' : '')} key={cap} title={why || undefined}>
                    <input type="checkbox" disabled={locked || cap === 'dashboard.view'}
                           checked={draft.capabilities.includes(cap)}
                           onChange={() => toggle(cap)} />
                    <span>
                      {CAP_LABEL[cap] ?? cap}
                      {why && <span className="field-hint">{why}</span>}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="row-form" style={{ marginTop: 12 }}>
              <button className="btn" disabled={busy !== null || !draft.name.trim()}
                      onClick={() => void run('save', async () => {
                        const r = await saveCustomRole({
                          orgId: orgId!,
                          id: draft.id || undefined,
                          name: draft.name.trim(),
                          description: draft.description,
                          capabilities: draft.capabilities,
                        });
                        setDraft(null);
                        // Refusals are surfaced, not swallowed. Believing a role
                        // grants something it does not is how it gets handed to
                        // the wrong person.
                        return r.refused.length
                          ? `${r.role.name} saved. ${r.refused.length} ${r.refused.length === 1 ? 'permission was' : 'permissions were'} refused: ${r.refused.join(', ')}.`
                          : `${r.role.name} saved.`;
                      })}>
                {busy === 'save' ? 'Saving...' : 'Save role'}
              </button>
              <button className="btn-link" onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </div>
        )}

        <p className="panel-note">
          A role somebody holds cannot be deleted &mdash; move them first. Deleting it and letting
          them fall back to something would change what those people can do without anybody choosing
          that, at a moment nobody logged.
        </p>
      </section>
    </div>
  );
}
